import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { KeySquare, Plus, Copy, Ban, Loader2 } from "lucide-react";

export function AdminSsoKeys() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);   // one-time secret reveal

  const load = () => api.get("/sso/keys").then(({ data }) => setKeys(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error("Name the key (e.g. the external product)"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/sso/keys", { name: name.trim() });
      setCreated(data); setName(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create key"); }
    finally { setCreating(false); }
  };
  const revoke = async (id) => {
    try { await api.delete(`/sso/keys/${id}`); toast.success("Key revoked"); load(); } catch { toast.error("Failed"); }
  };
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  return (
    <div className="space-y-4">
      <div className="glass-card p-4">
        <div className="flex items-center gap-2 mb-1"><KeySquare className="w-4 h-4 text-[color:var(--brand-ocean)]" /><h4 className="font-display text-base text-[color:var(--ink)]">Cross-product SSO keys</h4></div>
        <p className="text-xs text-[color:var(--ink-3)] leading-relaxed">External products exchange short-lived SSO tokens for EIP sessions via <code>POST /api/sso/exchange</code> using an API key + shared HS256 secret. Users get tokens from <code>POST /api/sso/token</code>.</p>
        <div className="flex gap-2 mt-3">
          <Input data-testid="sso-key-name" placeholder="Key name (e.g. Billing Portal)" value={name} onChange={(e) => setName(e.target.value)} className="bg-[color:var(--surface)] max-w-xs" />
          <Button data-testid="sso-key-create" onClick={create} disabled={creating} className="bg-[color:var(--brand-ocean)] text-white">{creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}Create key</Button>
        </div>
        {created && (
          <div data-testid="sso-key-reveal" className="mt-3 rounded-lg border border-[color:rgba(245,158,11,0.35)] bg-[color:rgba(245,158,11,0.08)] p-3 space-y-2">
            <div className="text-xs font-medium text-[color:var(--ink)]">Copy these now — shown only once</div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">API key</Label>
              <div className="flex items-center gap-1.5">
                <code className="text-[11px] font-mono break-all flex-1">{created.api_key}</code>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copy(created.api_key)}><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">Shared secret (HS256)</Label>
              <div className="flex items-center gap-1.5">
                <code className="text-[11px] font-mono break-all flex-1">{created.shared_secret}</code>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copy(created.shared_secret)}><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <Button size="sm" variant="ghost" className="text-xs" onClick={() => setCreated(null)}>Done, I saved them</Button>
          </div>
        )}
      </div>

      <div className="glass-card overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--surface-2)]"><tr>
            {["Name", "Key", "Audience", "Created", "Status", ""].map((h, i) => <th key={i} className="text-left px-3 py-2 font-medium text-[color:var(--ink-2)] text-xs whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} data-testid="sso-key-row" className="border-t border-[color:var(--glass-border)]">
                <td className="px-3 py-2 text-xs font-medium text-[color:var(--ink)]">{k.name}</td>
                <td className="px-3 py-2 text-xs font-mono text-[color:var(--ink-3)]">{k.api_key_hint}</td>
                <td className="px-3 py-2 text-xs text-[color:var(--ink-3)]">{k.audience}</td>
                <td className="px-3 py-2 text-xs text-[color:var(--ink-3)] whitespace-nowrap">{new Date(k.created_at).toLocaleDateString()}</td>
                <td className="px-3 py-2 text-xs">{k.revoked_at ? <Badge className="bg-[color:rgba(232,93,106,0.14)] text-[color:var(--danger)] border-0">Revoked</Badge> : <Badge className="bg-[color:rgba(0,194,168,0.15)] text-[color:var(--success)] border-0">Active</Badge>}</td>
                <td className="px-3 py-2 text-right">
                  {!k.revoked_at && <Button data-testid="sso-key-revoke" size="sm" variant="ghost" onClick={() => revoke(k.id)} className="text-[color:var(--danger)] h-7 text-xs"><Ban className="w-3.5 h-3.5 mr-1" />Revoke</Button>}
                </td>
              </tr>
            ))}
            {keys.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-xs text-[color:var(--ink-3)]">No SSO keys yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
